import { FaWhatsapp, FaInstagram, FaYoutube } from 'react-icons/fa';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { buildWaLink } from '@/config/brand';
import { useContent } from '@/lib/content';
import { scrollToId } from '@/lib/scroll';

const QUICK_LINKS = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Services', href: '#services' },
  { label: 'Our Work', to: '/portfolio' },
  { label: 'Contact', href: '#contact' },
];

const SERVICE_LINKS = ['Weddings', 'Birthdays', 'Baby Showers', 'Devotional', 'Corporate', 'Community'];

export const Footer = () => {
  const { brand } = useContent();
  return (
  <footer data-testid="footer" className="border-t border-white/10 bg-ink">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
      <div>
        <p className="font-serif text-3xl text-cream">Events <span className="italic text-champagne">by</span> AJ</p>
        <p className="mt-4 font-serif italic text-lg text-champagne/90">{brand.tagline}</p>
        <p className="mt-4 text-sm text-white/40 font-light leading-relaxed">
          Premium end-to-end event management in Hyderabad & Telangana.
        </p>
      </div>

      <div>
        <p className="caption-label mb-6">Quick Links</p>
        <ul className="space-y-3">
          {QUICK_LINKS.map((l) => (
            <li key={l.label}>
              {l.to ? (
                <Link data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-')}`} to={l.to}
                  className="text-sm text-white/60 hover:text-champagne transition-colors duration-300 font-light tracking-wide">
                  {l.label}
                </Link>
              ) : (
                <button data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, '-')}`} onClick={() => scrollToId(l.href)}
                  className="text-sm text-white/60 hover:text-champagne transition-colors duration-300 font-light tracking-wide">
                  {l.label}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="caption-label mb-6">Services</p>
        <ul className="space-y-3">
          {SERVICE_LINKS.map((s) => (
            <li key={s}>
              <button data-testid={`footer-service-${s.toLowerCase().replace(/\s/g, '-')}`} onClick={() => scrollToId('#services')}
                className="text-sm text-white/60 hover:text-champagne transition-colors duration-300 font-light tracking-wide">
                {s}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="caption-label mb-6">Contact</p>
        <ul className="space-y-4 text-sm text-white/60 font-light">
          <li>
            <a data-testid="footer-phone" href={`tel:${brand.phone}`} className="flex items-center gap-3 hover:text-champagne transition-colors">
              <Phone size={14} className="text-champagne" /> {brand.phoneDisplay}
            </a>
          </li>
          <li>
            <a data-testid="footer-whatsapp" href={buildWaLink(brand.whatsapp)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-champagne transition-colors">
              <FaWhatsapp size={14} className="text-champagne" /> WhatsApp Us
            </a>
          </li>
          <li>
            <a data-testid="footer-email" href={`mailto:${brand.email}`} className="flex items-center gap-3 hover:text-champagne transition-colors">
              <Mail size={14} className="text-champagne" /> {brand.email}
            </a>
          </li>
          <li className="flex items-center gap-3">
            <MapPin size={14} className="text-champagne" /> Hyderabad, Telangana
          </li>
        </ul>
        <div className="mt-6 flex gap-3">
          <a data-testid="footer-instagram" href={brand.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
            className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/60 hover:text-champagne hover:border-champagne transition-colors">
            <FaInstagram size={16} />
          </a>
          <a data-testid="footer-youtube" href={brand.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube"
            className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/60 hover:text-champagne hover:border-champagne transition-colors">
            <FaYoutube size={16} />
          </a>
        </div>
      </div>
    </div>

    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[11px] uppercase tracking-[0.25em] text-white/30">
          © {new Date().getFullYear()} Events by AJ. All rights reserved.
        </p>
        <p className="text-[11px] uppercase tracking-[0.25em] text-white/30">
          Best Event Planner in Hyderabad — TAA 2024 • Brands of India 2025
        </p>
      </div>
    </div>
  </footer>
  );
};
